import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { ThirdPartyCollectionOrder } from '../../../models/thirdPartyCollections/ThirdPartyCollectionOrder';
import ColdStoreOrderSelection from '../../../components/bookings/ColdStoreOrderSelection';
import { apiGet } from '../../../services/api';
import { storeToCompanyCode } from '../../../services/storeService';
import { StoreNumber } from '../../../models/stores/StoreNumber';

interface ThirdPartyOrderSelectionContainerProps {
    orderContainerClassName?: string;
    searchValue: string;
    selectedOrders: ThirdPartyCollectionOrder[];
    store: StoreNumber;
    onOrdersChange(
        selectedOrders: ThirdPartyCollectionOrder[],
        invalidOrderSelected?: boolean
    ): void;
    onSearchValueChange(searchValue: string): void;
}

const ThirdPartyOrderSelectionContainer: React.FC<ThirdPartyOrderSelectionContainerProps> = ({
    orderContainerClassName,
    searchValue,
    selectedOrders,
    store,
    onOrdersChange,
    onSearchValueChange
}) => {
    const [filteredOrders, setFilteredOrders] = useState<
        ThirdPartyCollectionOrder[]
    >([]);
    const [
        invalidOrderSelected,
        setInvalidOrderSelected
    ] = useState<ThirdPartyCollectionOrder | null>(null);

    const ordersToDisplay = useMemo(
        () =>
            filteredOrders.filter(
                o =>
                    !selectedOrders.some(order => order.id === o.id) &&
                    (!invalidOrderSelected ||
                        (invalidOrderSelected &&
                            o.id !== invalidOrderSelected.id))
            ) ?? [],
        [filteredOrders, selectedOrders, invalidOrderSelected]
    );

    useEffect(() => {
        const fetchFilteredOrders = async () => {
            const filteredOrders: ThirdPartyCollectionOrder[] = await apiGet(
                `ThirdPartyCollectionOrders/Company/${storeToCompanyCode(
                    store
                )}`,
                { SearchTerm: searchValue }
            );
            setFilteredOrders(filteredOrders);
        };

        if (searchValue.length > 1) {
            fetchFilteredOrders();
            return;
        }

        setFilteredOrders([]);
    }, [store, searchValue]);

    const handleInvalidOrderSelected = (
        selectedOrder: ThirdPartyCollectionOrder | null
    ) => {
        setInvalidOrderSelected(selectedOrder);
    };

    return (
        <ColdStoreOrderSelection
            orderContainerClassName={orderContainerClassName}
            orders={ordersToDisplay}
            searchValue={searchValue}
            selectedOrders={selectedOrders}
            onInvalidOrderSelected={handleInvalidOrderSelected}
            onOrdersChange={onOrdersChange}
            onOrderSearchValueChange={onSearchValueChange}
        />
    );
};

export default ThirdPartyOrderSelectionContainer;
